import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Text from '@/storybook/components/Text/Text';
import AILogoGradientIcon from '@/storybook/icons/AILogoGradientIcon';
import useCopyMessage from '../../useCopyMessage';
import AIBubbleDefaultActions from './AIBubble.DefaultActions';
import { TestIds } from './testIds';
import styles from './AIBubble.module.css';

export interface AIBubbleProps {
  children: ReactNode;
  timestamp?: string;
  actions?: ReactNode;
  showActionsByDefault?: boolean;
  copyValue?: string;
  hideActions?: boolean;
  className?: string;
  contentClassName?: string;
}

const isPrimitiveContent = (content: ReactNode): content is string | number =>
  typeof content === 'string' || typeof content === 'number';

const AIBubble: FC<AIBubbleProps> = ({
  children,
  timestamp,
  actions,
  showActionsByDefault = false,
  copyValue,
  hideActions = false,
  className,
  contentClassName,
}) => {
  const { t } = useTranslation();
  const { handleCopy, handlePointerEnter, isCopied } = useCopyMessage(copyValue ?? children);

  const resolvedActions = actions ?? (
    <AIBubbleDefaultActions onCopy={handleCopy} onPointerEnter={handlePointerEnter} isCopied={isCopied} />
  );
  const shouldRenderMetaRow = Boolean(timestamp) || !hideActions;
  const containerClassName = className ? `${styles.container} ${className}` : styles.container;
  const resolvedContentClassName = contentClassName ? `${styles.content} ${contentClassName}` : styles.content;

  return (
    <div
      className={containerClassName}
      aria-label={t('components.aiChatDrawer.aiBubble.ariaLabel')}
      data-testid={TestIds.bubble}
    >
      <div className={styles.avatarWrapper} data-testid={TestIds.avatar}>
        <AILogoGradientIcon className={styles.avatarIcon} />
      </div>

      <div className={resolvedContentClassName}>
        <div className={styles.bubble} data-testid={TestIds.message}>
          {isPrimitiveContent(children) ? (
            <Text as="span" className={styles.text}>
              {children}
            </Text>
          ) : (
            children
          )}
        </div>
        {shouldRenderMetaRow ? (
          <div className={styles.metaRow}>
            {timestamp && (
              <Text as="small" className={styles.timestamp} data-testid={TestIds.timestamp}>
                {timestamp}
              </Text>
            )}
            {!hideActions ? (
              <div
                className={`${styles.actions} ${showActionsByDefault ? styles.actionsVisible : styles.actionsHoverOnly}`}
                data-visibility={showActionsByDefault ? 'always' : 'hover'}
                data-testid={TestIds.actions}
              >
                {resolvedActions}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AIBubble;
